import { useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { useQuery } from '@connectrpc/connect-query'
import type { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { BiddingIntradayDesiredPriceQuerySchema } from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'
import { previewBiddingIntradayOrderFeeAdjustedPrices } from '@ebalance/proto/bffuser/v1/bidding_intraday_service-BiddingIntradayService_connectquery'
import { PreviewBiddingIntradayOrderFeeAdjustedPricesRequestSchema } from '@ebalance/proto/bffuser/v1/bidding_intraday_service_pb'
import { skipToken } from '@tanstack/react-query'

import type { IntradayFeeStateHandle } from '~/pages/IntradayBidding/hooks/useIntradayFeeState'
import {
  type FeeAutoConsiderMatrixSetting,
  type IntradayFeeEditBaseline,
  opensAsFeeFallbackOff,
  resolveIntradayFeeEditBaseline,
} from '~/pages/IntradayBidding/models/feeAutoConsider'

/** 編集モーダルを開いた時点の札。 手数料自動考慮の逆算の入力になる。 */
export type IntradayFeeEditSeed = {
  direction: Side
  /** 保存済みの入札価格 (Decimal string)。 */
  orderPriceYenPerKwh: string
}

type OpenedAs = 'unavailable' | 'off' | 'fee'

/**
 * 入札編集モーダルの手数料自動考慮の扱いを、 開いた時点で 1 回決める (AC-BIN-004b / AC-BIN-007)。
 * OFF として扱うかは作成と同じく開いた時の画面の状態と入札制限の設定で決め (応答を待たない)、
 * ON で単価を使える時だけ、 保存済みの入札価格から逆算した希望価格をプレビュー RPC に問い合わせる
 * (逆算は backend が持つ)。 `seed` が undefined の間 (成行・表示専用) は `none` を返す。
 *
 * 決めた扱いは閉じるまで変えない。 開いている間に状態や札の版が変わっても、 入力済みの価格が別の意味
 * (希望価格 / 入札価格) に読み替わらないようにするため。
 */
export function useIntradayFeeEditBaseline(
  seed: IntradayFeeEditSeed | undefined,
  feeState: IntradayFeeStateHandle,
  setting: FeeAutoConsiderMatrixSetting,
): IntradayFeeEditBaseline {
  const openedAsNow = (): OpenedAs =>
    opensAsFeeFallbackOff(feeState.state, setting)
      ? 'unavailable'
      : feeState.state.kind === 'fee'
        ? 'fee'
        : 'off'
  const [pinned, setPinned] = useState<
    { seed: IntradayFeeEditSeed; openedAs: OpenedAs } | undefined
  >(undefined)
  if (pinned === undefined && seed !== undefined) {
    setPinned({ seed, openedAs: openedAsNow() })
  }
  const [settled, setSettled] = useState<IntradayFeeEditBaseline | undefined>(
    undefined,
  )
  const asksDesiredPrice = pinned?.openedAs === 'fee' && settled === undefined
  const query = useQuery(
    previewBiddingIntradayOrderFeeAdjustedPrices,
    !asksDesiredPrice
      ? skipToken
      : create(PreviewBiddingIntradayOrderFeeAdjustedPricesRequestSchema, {
          desiredPriceQueries: [
            create(BiddingIntradayDesiredPriceQuerySchema, {
              direction: pinned.seed.direction,
              orderPriceYenPerKwh: { value: pinned.seed.orderPriceYenPerKwh },
            }),
          ],
        }),
    // 開くたびに取り直す: 前に開いた時の逆算の応答を cache から出さない。
    { throwOnError: false, gcTime: 0 },
  )
  const live: IntradayFeeEditBaseline | undefined =
    pinned === undefined
      ? undefined
      : pinned.openedAs === 'fee'
        ? resolveIntradayFeeEditBaseline(query)
        : { kind: pinned.openedAs }
  if (settled === undefined && live !== undefined && live.kind !== 'pending') {
    setSettled(live)
  }
  if (seed === undefined) return { kind: 'none' }
  return settled ?? live ?? { kind: 'pending' }
}
